import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Heart, Navigation, Building2, Zap, Star, Globe, Leaf, BadgeCheck } from "lucide-react";
import { useFavourites } from "../../context/FavouritesContext";
import { useFx } from "../../context/FxContext";
import { ShareButton } from "./ShareButton";
import Countdown from "./Countdown";
import { getRemaining } from "../../utils/countdown";
import { directionsUrl } from "../../utils/geo";
import { symbolFor } from "../../utils/currency";
import { categoryMeta } from "../../mock";
import { useLang } from "../../context/LanguageContext";
import { catLabel } from "../../utils/categoryLabel";
import { thumb } from "../../utils/image";
import { placeLabel } from "../../utils/place";
import { AdminDealActions } from "./AdminDealActions";
import { PriceVerifiedBadge } from "./PriceVerified";
import { QuietHoursBadge } from "./QuietHours";

const DealCard = ({ deal }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { isFav, toggleFav } = useFavourites();
  const { approx } = useFx();
  const [gone, setGone] = React.useState(false);
  const [archived, setArchived] = React.useState(false);
  const fav = isFav(deal.id);
  const { expired } = getRemaining(deal.expiresAt);
  const approxPrice = approx(deal.price != null ? deal.price : deal.save, deal.currency);
  const b0 = (deal.branches && deal.branches[0]) || null;
  const dirUrl = directionsUrl(b0 ? b0.lat : deal.lat, b0 ? b0.lng : deal.lng);
  const boosted = deal.boostedUntil && new Date(deal.boostedUntil) > new Date();
  const cat = categoryMeta(deal.category);
  const CatIcon = cat.icon;

  if (gone) return null;

  return (
    <div
      data-testid={`deal-card-${deal.id}`}
      // The whole card is the target — people tap the picture, the price, the whitespace.
      // Links and buttons inside still win, so Directions and the heart behave.
      onClick={(e) => { if (!e.target.closest("a,button")) navigate(`/deal/${deal.id}`); }}
      className={`group bg-card border rounded-3xl overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col h-full cursor-pointer ${archived ? "border-rose-400 opacity-60" : "border-border"}`}
    >
      <div className="relative h-44 shrink-0 overflow-hidden">
        <Link to={`/deal/${deal.id}`} className="block h-full">
          <img src={thumb(deal.image, 480)} alt={deal.title} loading="lazy" decoding="async" width="480" height="176" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-80" />
        </Link>
        <span className="absolute top-3 left-3 bg-[#16a34a] text-white text-xs font-extrabold px-2.5 py-1 rounded-lg shadow-md">{t("common.percentOff", { n: deal.discount })}</span>
        {boosted && (<span className="absolute top-11 left-3 bg-gradient-to-r from-[#f5b942] to-[#e0a92e] text-[#0f2a1a] text-[11px] font-extrabold px-2.5 py-1 rounded-lg shadow flex items-center gap-1" data-testid={`boosted-ribbon-${deal.id}`}><Zap size={12} className="fill-[#0f2a1a]" /> Boosted</span>)}
        {deal.featured && (<span className="absolute bottom-3 left-3 bg-[#0f2a1a] text-[#f5b942] text-[11px] font-extrabold px-2.5 py-1 rounded-lg shadow flex items-center gap-1" data-testid={`featured-ribbon-${deal.id}`}>★ Featured</span>)}
        {deal.nearExpiry && (<span className="absolute bottom-3 left-3 bg-rose-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow">{t("card.lastChance")}</span>)}
        {deal.shortDated && (<span className={`absolute ${deal.nearExpiry ? "bottom-10" : "bottom-3"} left-3 bg-[#16a34a] text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow flex items-center gap-1`} data-testid={`short-dated-ribbon-${deal.id}`}><Leaf size={11} /> {t("card.shortDated")}</span>)}
        {deal._distanceKm != null && (
          <span className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] font-semibold px-2 py-0.5 rounded-md flex items-center gap-1 backdrop-blur-sm"><Navigation size={11} /> {deal._distanceLabel}</span>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
          <AdminDealActions deal={{ ...deal, hidden: archived || deal.hidden }}
            onArchived={() => setArchived(true)} onDeleted={() => setGone(true)}
            onRestored={() => { setArchived(false); setGone(false); }} />
          {dirUrl && (
            <a href={dirUrl} target="_blank" rel="noopener noreferrer" data-testid={`directions-btn-${deal.id}`} onClick={(e) => e.stopPropagation()} title={t("card.directions")} className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 hover:bg-white transition-all text-slate-600">
              <Navigation size={15} />
            </a>
          )}
          <ShareButton deal={deal} iconSize={15} className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 hover:bg-white transition-all text-slate-600" />
          <button onClick={() => toggleFav(deal.id)} className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 hover:bg-white transition-all" aria-label={t("card.favourite")}>
            <Heart size={16} className={fav ? "fill-rose-500 text-rose-500" : "text-slate-600"} />
          </button>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <button type="button" onClick={() => navigate("/browse", { state: { category: cat.name } })} data-testid={`deal-category-${deal.id}`} title={t("dc.seeAllIn", { name: cat.name })} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full mb-1.5 hover:opacity-80 transition-opacity cursor-pointer" style={{ backgroundColor: cat.bg, color: cat.color }}>
          <CatIcon size={12} /> {catLabel(cat.name, t)}
        </button>
        <Link to={`/deal/${deal.id}`}>
          <h3 className="font-extrabold text-foreground leading-tight group-hover:text-[#16a34a] transition-colors flex items-center gap-1.5">
            {deal.business}
            {deal.verified && <BadgeCheck size={14} className="text-[#16a34a] shrink-0" title={t("vf.verifiedTag")} data-testid={`deal-verified-${deal.id}`} />}
          </h3>
        </Link>
        {deal.businessRating != null && (
          <span data-testid={`deal-card-rating-${deal.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold text-foreground">
            <Star size={12} className="fill-[#f5b942] text-[#f5b942]" /> {deal.businessRating}
            <span className="font-medium text-muted-foreground">· {t(deal.businessReviewCount === 1 ? "card.review" : "card.reviews", { n: deal.businessReviewCount })}</span>
          </span>
        )}
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{deal.title}</p>
        {deal.price != null && (
          <div className="mb-2"><PriceVerifiedBadge deal={deal} /></div>
        )}
        {deal.recurring && (
          <div className="mb-2"><QuietHoursBadge deal={deal} /></div>
        )}
        {deal.isOnline ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-semibold text-[#16a34a]" data-testid={`deal-card-online-${deal.id}`}><Globe size={13} /> {t("card.online")}</span>
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <MapPin size={13} className="text-[#16a34a] shrink-0" />
            <span className="truncate" title={`${deal.location || ""}${deal.country ? `, ${deal.country}` : ""}`}>{placeLabel(deal.location, deal.country)}</span>
            {deal.branches && deal.branches.length > 1 && (
              <span className="inline-flex items-center gap-1 text-[#16a34a] font-semibold shrink-0 whitespace-nowrap" data-testid="deal-card-branches"><Building2 size={12} /> {t("card.locations", { n: deal.branches.length })}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
          <span className={`flex items-center gap-1.5 text-xs font-medium ${expired ? "text-rose-500" : "text-orange-500"}`}>
            <Clock size={14} /> <Countdown expiresAt={deal.expiresAt} fallback={deal.timeLeft} />
          </span>
          {deal.save != null && (<span className="bg-[#e7f6ec] text-[#16a34a] text-xs font-bold px-2.5 py-1 rounded-lg">{t("card.save", { amount: `${symbolFor(deal.currency)}${deal.save}` })}</span>)}
        </div>
        {approxPrice && (
          <div className="mt-2 flex justify-end">
            <span data-testid="deal-card-approx" className="inline-flex items-center gap-1 rounded-full bg-[#e7f6ec] dark:bg-[#16341f] border border-[#16a34a]/30 text-[#16a34a] font-bold text-[11px] px-2 py-0.5">
              {approxPrice} <span className="font-medium opacity-80">{t("card.inYourCurrency")}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealCard;
